import { Circle, ArrowDown, Square, GitBranch, Plus } from "lucide-react";
import { ResizablePanel } from "../ui/resizable";
import { ScrollArea } from "../ui/scroll-area";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import type { Id } from "@/convex/src/_generated/dataModel";

interface FlowNode {
  _id: Id<"flowInteractionModules">;
  flowId: Id<"flows">;
  interactionModuleId: Id<"interactionModules">;
  label?: string;
}

interface FlowDriver {
  _id: Id<"flowDrivers">;
  type: string;
  name?: string;
}

interface FlowEdge {
  _id: Id<"flowEdges">;
  fromNodeId: Id<"flowInteractionModules">;
  toNodeId?: Id<"flowInteractionModules">;
  conditionKind?: string;
  priority: number;
  driver: FlowDriver | null;
}

interface InteractionModule {
  _id: Id<"interactionModules">;
  name: string;
  slug: string;
}

interface Flow {
  _id: Id<"flows">;
  name: string;
  entryNodeId?: Id<"flowInteractionModules">;
}

interface FlowGraphData {
  flow: Flow;
  nodes: FlowNode[];
  edges: FlowEdge[];
  interactionModules: InteractionModule[];
}

interface FlowStepListProps {
  flowGraph: FlowGraphData | null | undefined;
  onAddStep?: () => void;
}

interface StepNode {
  node: FlowNode;
  moduleName: string;
  outgoingEdges: FlowEdge[];
}

export function FlowStepList({ flowGraph, onAddStep }: FlowStepListProps) {
  if (!flowGraph) {
    return (
      <ResizablePanel defaultSize={60} minSize={30}>
        <div className="h-full flex flex-col bg-background">
          <div className="px-4 py-3 border-b border-border">
            <h3 className="text-xs font-semibold text-foreground">Steps</h3>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <p className="text-xs text-muted-foreground">
              Select a flow to see steps
            </p>
          </div>
        </div>
      </ResizablePanel>
    );
  }

  const { flow, nodes, edges, interactionModules } = flowGraph;

  // Build lookup maps
  const moduleMap = new Map(interactionModules.map((m) => [m._id, m]));
  const nodeMap = new Map(nodes.map((n) => [n._id, n]));

  // Build edge map: fromNodeId -> edges[]
  const edgesByFromNode = new Map<string, FlowEdge[]>();
  for (const edge of edges) {
    const key = edge.fromNodeId;
    if (!edgesByFromNode.has(key)) {
      edgesByFromNode.set(key, []);
    }
    edgesByFromNode.get(key)!.push(edge);
  }

  // Sort edges by priority
  for (const edgeList of edgesByFromNode.values()) {
    edgeList.sort((a, b) => a.priority - b.priority);
  }

  // Get node label with fallback to module name
  const getNodeLabel = (node: FlowNode): string => {
    if (node.label) return node.label;
    const mod = moduleMap.get(node.interactionModuleId);
    return mod?.name || "(Unknown Module)";
  };

  // Get module name
  const getModuleName = (node: FlowNode): string => {
    const mod = moduleMap.get(node.interactionModuleId);
    return mod?.name || "(Unknown)";
  };

  // Build linear step list by walking from entry node
  // This handles the simple case; for complex branches, we'll show all paths
  const buildStepList = (): StepNode[] => {
    const steps: StepNode[] = [];
    const visited = new Set<string>();

    // If no entry node, show all nodes
    if (!flow.entryNodeId) {
      for (const node of nodes) {
        steps.push({
          node,
          moduleName: getModuleName(node),
          outgoingEdges: edgesByFromNode.get(node._id) || [],
        });
      }
      return steps;
    }

    // BFS from entry node to build ordered list
    const queue: Id<"flowInteractionModules">[] = [flow.entryNodeId];

    while (queue.length > 0) {
      const nodeId = queue.shift()!;
      if (visited.has(nodeId)) continue;
      visited.add(nodeId);

      const node = nodeMap.get(nodeId);
      if (!node) continue;

      const outgoingEdges = edgesByFromNode.get(nodeId) || [];
      steps.push({
        node,
        moduleName: getModuleName(node),
        outgoingEdges,
      });

      // Add next nodes to queue
      for (const edge of outgoingEdges) {
        if (edge.toNodeId && !visited.has(edge.toNodeId)) {
          queue.push(edge.toNodeId);
        }
      }
    }

    // Add any unvisited nodes at the end (disconnected nodes)
    for (const node of nodes) {
      if (!visited.has(node._id)) {
        steps.push({
          node,
          moduleName: getModuleName(node),
          outgoingEdges: edgesByFromNode.get(node._id) || [],
        });
      }
    }

    return steps;
  };

  const steps = buildStepList();

  return (
    <ResizablePanel defaultSize={60} minSize={30}>
      <div className="h-full flex flex-col bg-background">
        <div className="px-4 py-3 border-b border-border flex items-center gap-2">
          <GitBranch className="h-3 w-3 text-muted-foreground" />
          <h3 className="text-xs font-semibold text-foreground">Steps</h3>
        </div>

        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full">
            <div className="p-4">
              {steps.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mb-3">
                    <GitBranch className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    No steps in this flow yet.
                  </p>
                  {onAddStep && (
                    <Button onClick={onAddStep} variant="outline" size="sm">
                      <Plus className="h-3 w-3 mr-2" />
                      Add step
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-0">
                  {steps.map((step, index) => {
                    const isEntry = flow.entryNodeId === step.node._id;
                    const isLast = index === steps.length - 1;
                    const hasTerminalEdge = step.outgoingEdges.some(
                      (e) => !e.toNodeId,
                    );

                    return (
                      <div key={step.node._id} className="relative">
                        {/* Node */}
                        <div className="flex items-start gap-3">
                          {/* Vertical line connector */}
                          <div className="flex flex-col items-center">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                isEntry
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-muted border border-border"
                              }`}
                            >
                              <Circle className="h-3 w-3" />
                            </div>
                            {/* Connector line */}
                            {!isLast && (
                              <div className="w-0.5 h-full min-h-[40px] bg-border" />
                            )}
                          </div>

                          {/* Node content */}
                          <div className="flex-1 pb-4">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium">
                                {getNodeLabel(step.node)}
                              </span>
                              {isEntry && (
                                <Badge
                                  variant="default"
                                  className="text-[10px] px-1.5 py-0"
                                >
                                  Entry
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground font-mono">
                              {step.moduleName}
                            </p>

                            {/* Outgoing edges */}
                            {step.outgoingEdges.length > 0 && (
                              <div className="mt-3 space-y-2">
                                {step.outgoingEdges.map((edge) => {
                                  const targetNode = edge.toNodeId
                                    ? nodeMap.get(edge.toNodeId)
                                    : null;
                                  const targetLabel = targetNode
                                    ? getNodeLabel(targetNode)
                                    : "(End)";

                                  return (
                                    <div
                                      key={edge._id}
                                      className="flex items-center gap-2 text-xs text-muted-foreground"
                                    >
                                      <ArrowDown className="h-3 w-3 flex-shrink-0" />
                                      <span>
                                        {edge.conditionKind ? (
                                          <>
                                            <code className="bg-muted px-1 rounded">
                                              {edge.conditionKind}
                                            </code>
                                            {" → "}
                                          </>
                                        ) : (
                                          "→ "
                                        )}
                                        <span className="font-medium text-foreground">
                                          {targetLabel}
                                        </span>
                                      </span>
                                      {edge.driver && (
                                        <Badge
                                          variant="outline"
                                          className="text-[10px] px-1 py-0"
                                        >
                                          {edge.driver.type}
                                        </Badge>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Terminal indicator */}
                        {isLast && hasTerminalEdge && (
                          <div className="flex items-start gap-3 mt-2">
                            <div className="flex flex-col items-center">
                              <div className="w-8 h-8 rounded-md flex items-center justify-center bg-muted border border-border">
                                <Square className="h-3 w-3" />
                              </div>
                            </div>
                            <div className="flex items-center h-8">
                              <span className="text-xs text-muted-foreground font-medium">
                                End
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Add step button at bottom */}
                  {onAddStep && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <Button
                        onClick={onAddStep}
                        variant="outline"
                        size="sm"
                        className="w-full"
                      >
                        <Plus className="h-3 w-3 mr-2" />
                        Add step
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </div>
    </ResizablePanel>
  );
}
